import { Injectable, inject } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { AmplifyApiService } from 'src/app/core/services/amplify-api.service';
import { AmplifyAuthService } from 'src/app/core/services/amplify-auth.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import {
  DeepDeleteCampaignSuccess,
  LoadAffectedCampaignsSuccess,
  LoadCampaignsSuccess,
  LoadMyCampaignsSuccess,
  ManageCampaign,
  PostSaveCampaignComplete,
  PostSaveCampaignFail,
  PromotionSuccess
} from 'src/app/features/campaigns/redux/campaign.actions';
import { InsightsState } from 'src/app/features/insights/redux/insights.state';
import { Campaign } from 'src/app/models/campaign';
import { Content } from 'src/app/models/content';
import { Insight } from 'src/app/models/insight';
import { StatisticsModel } from 'src/app/models/statistics';
import { APIS } from 'src/app/shared/constants/api-constants';
import { ENVIRONMENT } from 'src/environments/environment';

@Injectable({ providedIn: 'any' })
export class CampaignService {
  public campaigns_api = APIS.CAMPAIGNS;
  public jobs_api = APIS.CAMPAIGN_JOBS;
  public individual_jobs_api = APIS.SINGLE_JOBS;
  public campaign_deliveries_api = APIS.CAMPAIGN_DELIVERIES;

  public insight$: Observable<Insight[] | undefined> = inject(Store).select(InsightsState.allInsights);
  public inactiveInsight$: Observable<Insight[] | undefined> = inject(Store).select(InsightsState.insightsInactive);

  public signedRequests: any;
  public owner = '';
  public allKeys: string[] = [];
  public selectedKeys: string[] = [];
  public type = 'campaigns';

  public insights?: Insight[];
  public inactiveInsights?: Insight[];

  public campaigns: any;

  // promotion process flags
  public promotionResponse: any = false;
  public promotionError: any = false;
  public promotionSpinner: any = false;

  public statModel = new StatisticsModel();

  constructor(
    private store: Store,
    private toastrService: NbToastrService,
    private utilityService: UtilityService,
    private apiService: AmplifyApiService,
    private amplifyAuthService: AmplifyAuthService
  ) {
    this.insight$.subscribe((data) => (this.insights = data));
    this.inactiveInsight$.subscribe((data) => (this.inactiveInsights = data));
  }

  public loadCampaignsAmplify(reload = false) {
    this.apiService
      .get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns`)
      .then((response) => {
        if (reload) {
          this.toastrService.show(status, $localize`Fetching Campaigns Complete`, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadCampaignsSuccess(response));
      })
      .catch((error) => {
        this.toastrService.show(status, $localize`Fetching Campaigns Failed`, {
          status: 'danger'
        });
        console.error(error);
      });
  }

  public loadMyCampaignsAmplify(email: string, reload = false) {
    this.apiService
      .get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns`, { queryStringParameters: { email: email } })
      .then((response) => {
        if (reload) {
          this.toastrService.show(status, $localize`Fetching My Campaigns Complete`, {
            status: 'success'
          });
        }
        response.campaigns.forEach((element: Campaign) => {
          this.addInsightInfo(element);
        });
        this.store.dispatch(new LoadMyCampaignsSuccess(response));
      })
      .catch((error) => {
        this.toastrService.show(status, $localize`Fetching My Campaigns Failed`, {
          status: 'danger'
        });
        console.error(error);
      });
  }

  public loadAffectedCampaignsAmplify(campaign: Campaign, reload = false) {
    this.fetchAffectedCampaigns(
      this.apiService.get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns/deep-delete`, {
        queryStringParameters: { campaign_id: campaign.id }
      }),
      $localize`Fetching Affected Campaigns Complete`,
      $localize`Fetching Affected Campaigns Failed`,
      reload
    );
  }

  public loadAffectedCampaignsByInsightAmplify(insight: Insight, reload = false) {
    if (insight.id === undefined) {
      this.toastrService.danger('', $localize`Missing Insight ID to fetch affected campaigns by insight`);
      return;
    }
    this.fetchAffectedCampaigns(
      this.apiService.get(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns/deep-delete`, {
        queryStringParameters: { trigger_id: insight.id }
      }),
      $localize`Fetching Affected Campaigns By Insight Complete`,
      $localize`Fetching Affected Campaigns By Insight Failed`,
      reload
    );
  }

  //Activate is used when user wants to activate Campaign and Insight from View Campaign component
  public postCampaignsAmplify(campaign: Campaign, edit?: boolean, activate?: boolean) {
    this.apiService
      .post(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns`, { body: campaign })
      .then((response) => {
        if (activate) {
          this.store.dispatch(new ManageCampaign(response));
          this.toastrService.show(status, $localize`Campaign activation complete`, {
            status: 'success'
          });
        } else if (activate !== undefined) {
          this.store.dispatch(new ManageCampaign(response));
          this.toastrService.show(status, $localize`Campaign deactivation complete`, {
            status: 'success'
          });
        } else {
          this.store.dispatch(new PostSaveCampaignComplete(edit, response));
        }
      })
      .catch((error) => {
        this.store.dispatch(new PostSaveCampaignFail(error));
      });
  }

  public deepDeleteCampaignAmplify(payload: { campaign_id: string; deleted_by?: string }) {
    this.apiService
      .post(this.campaigns_api, `/${ENVIRONMENT.name}/campaigns/deep-delete`, { body: payload })
      .then(() => {
        this.toastrService.show(status, $localize`Deep Delete Campaign ${payload.campaign_id} Success`, {
          status: 'success'
        });
        this.store.dispatch(new DeepDeleteCampaignSuccess(payload));
      })
      .catch((error) => {
        console.error(error);
        const message = this.utilityService.convertErrorToMessage(error);
        this.toastrService.show(status, $localize`Deep Delete Campaign ${payload.campaign_id} Failed: ${message}`, {
          status: 'danger'
        });
      });
  }

  public async postPromoteCampaign(campaign_id: string, username: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await this.amplifyAuthService.fetchAuthSession().then((session) => {
      const accessToken = session.tokens?.accessToken;
      const token = accessToken?.toString();
      if (token) {
        this.postPromoteCampaignEndpoint(campaign_id, username, token);
      } else {
        this.toastrService.danger('', $localize`Failed to get session token`);
      }
    });
  }

  public postPromoteCampaignEndpoint(campaign_id: string, username: string, token: string): any {
    this.promotionSpinner = true;
    this.apiService
      .post(this.campaigns_api, `/${ENVIRONMENT.name}/promotion?token=${token}`, {
        body: { campaign_id, promoter_username: username }
      })
      .then((response) => {
        this.promotionResponse = response;
        this.promotionSpinner = false;
        this.toastrService.show(status, $localize`Campaign has been promoted.`, {
          status: 'success'
        });
        this.store.dispatch(new PromotionSuccess('success'));
      })
      .catch((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        ((this.promotionError = this.utilityService.convertErrorToMessage(error)), (this.promotionSpinner = false));
      });
  }

  public getCampaignDeliveries(campaign_id: string, startDate: number, endDate: number) {
    this.toastrService.show(status, $localize`Fetching deliveries for ${campaign_id}`, {
      status: 'info'
    });
    return this.apiService.get(this.campaign_deliveries_api, `/${ENVIRONMENT.name}/deliveries`, {
      queryStringParameters: {
        campaign_id,
        start_date: startDate,
        end_date: endDate
      }
    });
  }

  public addInsightInfo(item: Campaign) {
    const insight = this.insights?.find(({ id }) => id?.toString() === item.trigger_id);
    if (insight) {
      item.insight = insight;
    }

    return item;
  }

  private fetchAffectedCampaigns(
    promise: Promise<any>,
    successMessage: string,
    failureMessage: string,
    reload?: boolean
  ) {
    promise
      .then((response) => {
        if (reload) {
          this.toastrService.show(status, successMessage, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadAffectedCampaignsSuccess(response));
      })
      .catch((error) => {
        this.toastrService.show(status, failureMessage, {
          status: 'danger'
        });
        console.error(error);
      });
  }

  public getAverageExecDuration(campaign: Campaign): string {
    const average = this.statModel.getAverageExecDuration(campaign.total_duration, campaign.executions);
    if (typeof average === 'string') {
      return average.replace(/\(\d* seconds\)/, '');
    } else {
      return 'N/A';
    }
  }

  public isValidCampaignType(campaign: Campaign, content: Content): boolean {
    if (campaign.campaign_type === undefined) {
      return true;
    }
    const primeTypes = ['ContextualMessage', 'MultiPageContextualMessage', 'eCommerce', 'MapDirty', 'DataCollection'];

    if (this.utilityService.isPrimeMinAppVersion(content?.min_app_version)) {
      return primeTypes.includes(campaign.campaign_type);
    }
    return true;
  }
}
